import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { environment } from 'src/environments/environment';
import { FichaDaObraService } from '../service/ficha-da-obra.service';
import { TarefasRelatosService } from './tarefas-relatos.service';

@Component({
  selector: 'app-tarefas-relatos',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './tarefas-relatos.component.html',
  styleUrl: './tarefas-relatos.component.scss',
})
export class TarefasRelatosComponent implements OnChanges {
  private lastIdObraConsulted: number | null = null;
  @Input() idObra: number | null = null;
  @Input() somenteLeitura = false;
  @Output() editar = new EventEmitter<any>();
  @Output() editarRelato = new EventEmitter<any>();
  @Output() concluir = new EventEmitter<any>();
  @Output() abrirDescarteTarefa = new EventEmitter<any>();
  @Output() abrirDescarteRelato = new EventEmitter<any>();

  BASE_IMG_URL: string = environment.BASE_IMG_URL;
  userId: string;
  tarefas: any[] = [];

  tarefaParaDescarte: any = null;
  relatoParaDescarte: any = null;
  private _fichaDaObraService = inject(FichaDaObraService);
  private _tarefasRelatosService = inject(TarefasRelatosService);

  constructor() {
    this.userId = localStorage.getItem('userId');
  }

  ngOnInit(): void {
    if (this.idObra && this.idObra !== this.lastIdObraConsulted) {
      this.carregarTarefasRelatos();
      this.lastIdObraConsulted = this.idObra;
    }
    this._tarefasRelatosService.tarefasRelatosAtualizados$.subscribe(() => {
      if (this.idObra) {
        this.carregarTarefasRelatos();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['idObra'] &&
      changes['idObra'].currentValue !== undefined &&
      changes['idObra'].currentValue !== this.lastIdObraConsulted &&
      changes['idObra'].currentValue
    ) {
      this.carregarTarefasRelatos();
      this.lastIdObraConsulted = changes['idObra'].currentValue;
    }
  }

  editarTarefa(tarefa: any) {
    const contatoInfo = tarefa.contactID
      ? {
          id: tarefa.contactID,
          cargo: tarefa.contactRole || '',
          nome: tarefa.contactName || '',
          email: tarefa.contactEmail || '',
        }
      : null;

    this.editar.emit({
      ...tarefa,
      descricao: tarefa.descricao,
      data: tarefa.data,
      hora: tarefa.horaInput,
      raw: tarefa.raw,
      contatoInfo,
    });
  }

  onEditarRelato(tarefa: any) {
    this.editarRelato.emit({
      ...tarefa,
      descricao: tarefa.descricao,
      titulo: tarefa.titulo,
      data: tarefa.data,
      hora: tarefa.horaInput,
      raw: tarefa.raw,
    });
  }

  concluirTarefa(tarefa: any) {
    if (!tarefa.raw?.taskID) return;
    this._fichaDaObraService
      .updateStatusTarefa(tarefa.raw.taskID, 3)
      .subscribe({
        next: () => {
          const updatedTarefa = { ...tarefa, status: 3 };
          this.tarefas = this.tarefas.map((t) =>
            t === tarefa ? updatedTarefa : t
          );

          this.concluir.emit({ id: tarefa.raw.taskID });
          this.tarefas = [...this.tarefas];
        },
        error: () => {
          console.error('Erro ao concluir tarefa');
        },
      });
  }

  getStatusText(tarefa: any): string {
    if (tarefa.status === 3) {
      return 'Tarefa conclu\u00edda';
    } else if (tarefa.status === 2) {
      return tarefa.tipo === 'relato' ? 'Relato editado' : 'Tarefa editada';
    } else if (tarefa.status === 1) {
      return tarefa.tipo === 'relato' ? 'Relato criado' : 'Tarefa criada';
    } else if (tarefa.status === 4) {
      return tarefa.tipo === 'relato'
        ? 'Relato exclu\u00eddo'
        : 'Tarefa exclu\u00edda';
    }
    return '';
  }

  onDeletarTarefa(tarefa: any) {
    this.abrirDescarteTarefa.emit(tarefa);
  }

  onDeletarRelato(tarefa: any) {
    this.abrirDescarteRelato.emit(tarefa);
  }

  carregarTarefasRelatos() {
    if (!this.idObra) return;
    this._fichaDaObraService.listarTarefasRelatos(this.idObra).subscribe({
      next: (response) => {
        const tarefasApi = response?.data || [];

        const mapped = tarefasApi.map((item: any) => {
          let dia = '',
            mes = '',
            hora = '',
            data = '';
          let horaInput = '',
            dataInput = '';
          let dateObj: Date | null = null;
          if (item.followUp || item.creationDate) {
            const dateStr = item.followUp || item.creationDate;
            dateObj = new Date(dateStr);
            dia = String(dateObj.getDate()).padStart(2, '0');
            const meses = [
              'JAN',
              'FEV',
              'MAR',
              'ABR',
              'MAI',
              'JUN',
              'JUL',
              'AGO',
              'SET',
              'OUT',
              'NOV',
              'DEZ',
            ];
            mes = meses[dateObj.getMonth()];
            hora = dateObj.toTimeString().slice(0, 5);

            dataInput = dateObj.toISOString().slice(0, 10);
            horaInput = dateObj.toTimeString().slice(0, 5);
          }
          const isRelato = (item.typeDescription || '')
            .toLowerCase()
            .includes('relato');

          const isDescartada =
            item.status === 2 || item.status === 'descartado';
          const isCriada = !item.raw || !item.raw.id;

          const tarefa = {
            dia,
            mes,
            autor: item.authorName,
            hora,
            titulo:
              item.actionDescription ||
              item.reportTitle ||
              (isRelato ? 'Relato' : 'Tarefa'),
            descricao: item.description,
            tipo: isRelato ? 'relato' : 'tarefa',
            status: item.status,
            descartada: isDescartada,
            followUp: item.followUp,
            creationDate: item.creationDate,
            data: dataInput,
            horaInput: horaInput,
            isEdited: item.isEdited === true,

            raw: {
              ...item,
              workID: item.workID ?? this.idObra,
              id: item.id ?? item.taskID ?? item.actionID,
              actionID: item.actionID ?? item.id,
              responsibleID: item.responsibleID,
              source: item.raw?.source ?? null,
            },
            criada: isCriada,
          };

          return tarefa;
        });

        const tarefasAFazer = mapped
          .filter((t) => t.tipo === 'tarefa' && t.status !== 0 && !t.descartada)
          .sort(
            (a, b) =>
              new Date(a.followUp).getTime() - new Date(b.followUp).getTime()
          );
        const relatos = mapped
          .filter((t) => t.tipo === 'relato')
          .sort(
            (a, b) =>
              new Date(b.creationDate).getTime() -
              new Date(a.creationDate).getTime()
          );
        const tarefasConcluidas = mapped
          .filter((t) => t.tipo === 'tarefa' && t.status === 0)
          .sort(
            (a, b) =>
              new Date(b.followUp).getTime() - new Date(a.followUp).getTime()
          );
        const tarefasDescartadas = mapped
          .filter((t) => t.tipo === 'tarefa' && t.descartada)
          .sort(
            (a, b) =>
              new Date(b.followUp).getTime() - new Date(a.followUp).getTime()
          );
        this.tarefas = [
          ...tarefasAFazer,
          ...relatos,
          ...tarefasConcluidas,
          ...tarefasDescartadas,
        ];
      },
      error: () => {
        this.tarefas = [];
      },
    });
  }
}
